import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { GameSession } from "./gameSession";
import type { usePlayerState } from "../hooks/usePlayerState";
import {
  MODE_BY_ID,
  MODE_COUNTDOWN_MS,
  MODE_FEEDBACK_DURATION_MS,
} from "../state/modeConfig";

/**
 * Owns a GameSession instance for the duration of one mode session. Forces
 * React re-renders after any mutation via useReducer bump(). Auto-advances
 * out of the feedback phase after the mode's per-mode feedback duration so
 * individual modes don't each have to re-implement the timer.
 */
export function useGameSession(
  modeId: string,
  player: ReturnType<typeof usePlayerState>,
) {
  const sessionRef = useRef<GameSession | null>(null);
  const [, bump] = useReducer((x: number) => x + 1, 0);

  if (sessionRef.current === null) {
    // GameSession.enterResults mutates PlayerState fields directly
    // (sessionsPlayed, modeHighScores, modeDifficulty) and then calls the
    // persist callback. Wire it to player.persist so the save actually
    // lands in localStorage — without this the session would correctly
    // increment in-memory state but lose everything on page reload.
    sessionRef.current = new GameSession(modeId, player.state, player.persist);
  }
  const session = sessionRef.current;

  const startPlaying = useCallback(() => {
    session.startPlaying();
    bump();
  }, [session]);

  const submitAnswer = useCallback(
    (correct: boolean) => {
      session.submitAnswer(correct);
      bump();
    },
    [session],
  );

  const advanceFromFeedback = useCallback(() => {
    session.advanceFromFeedback();
    bump();
  }, [session]);

  const finishCountdown = useCallback(() => {
    session.finishCountdown();
    bump();
  }, [session]);

  const applyMidRoundPenalty = useCallback(
    (penalty: number) => {
      session.applyMidRoundPenalty(penalty);
      bump();
    },
    [session],
  );

  /**
   * Live-tune the session's difficulty from the Instructions screen.
   * Clamps to [1, maxDifficulty] per the mode's config, persists to the
   * underlying PlayerState so it stays in effect next time the tester
   * opens this mode, and bumps to re-render the Instructions display.
   */
  const setDifficulty = useCallback(
    (next: number) => {
      const max = MODE_BY_ID[modeId]?.maxDifficulty ?? 15;
      const clamped = Math.max(1, Math.min(Math.floor(next), max));
      session.difficulty = clamped;
      player.setDifficulty(modeId, clamped);
      bump();
    },
    [session, player, modeId],
  );

  // Auto-advance out of feedback phase after the mode's feedback duration
  // (per-mode pacing, matching desktop/iOS — e.g. Rule Switch 500ms,
  // N-Back / Go-No-Go 1800ms).
  useEffect(() => {
    if (session.phase.kind !== "feedback") return;
    const duration = MODE_FEEDBACK_DURATION_MS[modeId] ?? 1200;
    const id = window.setTimeout(() => {
      session.advanceFromFeedback();
      bump();
    }, duration);
    return () => window.clearTimeout(id);
  }, [session, session.phase, modeId]);

  // Auto-begin the trial after the mode's "get ready" countdown elapses, so
  // each mode doesn't re-implement the timer. Mirrors the feedback effect.
  useEffect(() => {
    if (session.phase.kind !== "countdown") return;
    const duration = MODE_COUNTDOWN_MS[modeId] ?? 0;
    const id = window.setTimeout(() => {
      session.finishCountdown();
      bump();
    }, duration);
    return () => window.clearTimeout(id);
  }, [session, session.phase, modeId]);

  return useMemo(
    () => ({
      session,
      startPlaying,
      submitAnswer,
      advanceFromFeedback,
      finishCountdown,
      applyMidRoundPenalty,
      setDifficulty,
    }),
    [
      session,
      startPlaying,
      submitAnswer,
      advanceFromFeedback,
      finishCountdown,
      applyMidRoundPenalty,
      setDifficulty,
    ],
  );
}
